export const REPOSITORY_EXPLORER_ROOT_PATH = '__repository_explorer_root__';

/** Prefijos acumulados con `/` inicial (rutas OAIS absolutas para API y sessionStorage). */
export function buildRepositoryPathPrefixes(path: string): string[] {
  const normalized = normalizeRepositoryExplorerPath(path);
  if (!normalized || normalized === REPOSITORY_EXPLORER_ROOT_PATH) {
    return [];
  }
  const segments = normalized.split('/').filter(Boolean);
  const prefixes: string[] = [];
  for (let i = 0; i < segments.length; i++) {
    prefixes.push(`/${segments.slice(0, i + 1).join('/')}`);
  }
  return prefixes;
}

/** Carpeta padre para el botón Volver (un nivel arriba; vacío = raíz del listado). */
export function getParentRepositoryExplorerPath(path: string): string {
  const normalized = normalizeRepositoryExplorerPath(path);
  if (!normalized || normalized === REPOSITORY_EXPLORER_ROOT_PATH || normalized === '/') {
    return '';
  }
  const segments = normalized.split('/').filter(Boolean);
  if (segments.length <= 1) {
    return '';
  }
  segments.pop();
  return `/${segments.join('/')}`;
}

/**
 * Rutas del filesystem fuera de `/Repositorio` (p. ej. `/home`, `/home/egs/datavault`).
 * No son navegables en Bóveda: Volver debe caer en la raíz lógica `''`.
 */
export function isOutsideRepositoryBrowseRoot(path: string): boolean {
  const normalized = normalizeRepositoryExplorerPath(path);
  if (!normalized || normalized === REPOSITORY_EXPLORER_ROOT_PATH || normalized === '/') {
    return false;
  }
  const lower = normalized.toLowerCase();
  if (
    lower.includes('/repositorio/') ||
    lower.endsWith('/repositorio') ||
    lower === '/repositorio' ||
    lower === 'repositorio'
  ) {
    return false;
  }
  // Prefijos típicos del host OAIS encima de Repositorio.
  if (
    /^\/?home(\/|$)/i.test(normalized) ||
    /\/datavault(\/|$)/i.test(normalized) ||
    /^\/?datavault(\/|$)/i.test(normalized)
  ) {
    return true;
  }
  return false;
}

/** Carpeta `/…/Repositorio` (último nivel de carpetas antes de la raíz lógica). */
export function isRepositoryBrowseRootFolder(path: string): boolean {
  const normalized = normalizeRepositoryExplorerPath(path);
  if (!normalized) {
    return false;
  }
  const lower = normalized.toLowerCase().replace(/\/+$/, '');
  return lower.endsWith('/repositorio') || lower === '/repositorio' || lower === 'repositorio';
}

/** Fuerza rutas ilegales (fuera de Repositorio) a la raíz de Bóveda. */
export function clampRepositoryBrowsePath(path: string): string {
  const normalized = normalizeRepositoryExplorerPath(path);
  if (!normalized || normalized === REPOSITORY_EXPLORER_ROOT_PATH) {
    return '';
  }
  if (isOutsideRepositoryBrowseRoot(normalized)) {
    return '';
  }
  return normalized;
}

/** Ruta relativa dentro de un ZIP (sin `/` inicial). */
export function normalizeZipInternalPath(path: string): string {
  return (path ?? '')
    .trim()
    .replace(/\\/g, '/')
    .replace(/\/+/g, '/')
    .replace(/^\/+/, '')
    .replace(/\/+$/, '');
}

/** Evita usar rutas del filesystem OAIS como ruta interna del ZIP. */
export function isLikelyRepositoryFilesystemPath(path: string): boolean {
  const normalized = (path ?? '').trim().replace(/\\/g, '/');
  if (!normalized) {
    return false;
  }
  if (/^\/?home(\/|$)/i.test(normalized)) {
    return true;
  }
  if (/\/Repositorio(\/|$)/i.test(normalized) || /^Repositorio(\/|$)/i.test(normalized)) {
    return true;
  }
  if (/\.zip\//i.test(normalized)) {
    return true;
  }
  return /\/datavault\//i.test(normalized) || /^datavault\//i.test(normalized);
}

/** Obtiene solo la parte relativa dentro del ZIP (descarta rutas OAIS erróneas del API). */
export function extractZipInternalRelativePath(path: string | null | undefined): string {
  const normalized = normalizeZipInternalPath(path ?? '');
  if (!normalized) {
    return '';
  }
  const lower = normalized.toLowerCase();
  // API Vault: ".../archivo.zip::data/file.pdf" (doble dos puntos)
  const zipColon = '.zip::';
  const colonIdx = lower.indexOf(zipColon);
  if (colonIdx >= 0) {
    return normalizeZipInternalPath(normalized.slice(colonIdx + zipColon.length));
  }
  // Variante con barra: ".../archivo.zip/data/file.pdf"
  const zipSlash = '.zip/';
  const slashIdx = lower.indexOf(zipSlash);
  if (slashIdx >= 0) {
    return normalizeZipInternalPath(normalized.slice(slashIdx + zipSlash.length));
  }
  if (isLikelyRepositoryFilesystemPath(normalized)) {
    return '';
  }
  return normalized;
}

/** @deprecated Usar isLikelyRepositoryFilesystemPath */
export function isRepositoryOaisAbsolutePath(path: string): boolean {
  return isLikelyRepositoryFilesystemPath(path);
}

/** Segmento interno fiable al abrir carpetas/archivos dentro de un ZIP. */
export function resolveZipInternalRowSegment(item: {
  name?: string;
  path?: string;
  internal_path?: string | null;
}): string {
  const fromInternal = extractZipInternalRelativePath(item.internal_path);
  if (fromInternal) {
    return fromInternal;
  }
  const fromPath = extractZipInternalRelativePath(item.path);
  if (fromPath) {
    return fromPath;
  }
  return normalizeZipInternalPath(item.name ?? '');
}

/** Limpia la ruta interna persistida antes de llamar al API de listado ZIP. */
export function sanitizeZipInternalPathForList(path: string | undefined | null): string | undefined {
  const relative = extractZipInternalRelativePath(path);
  return relative || undefined;
}

/**
 * Normaliza `internal_path` para GET /zip/list (sin `/` final; alineado al front React).
 */
export function formatZipListInternalPath(internalPath: string | undefined | null): string | undefined {
  return sanitizeZipInternalPathForList(internalPath);
}

/** Sube un nivel dentro del ZIP; vacío = raíz interna del archivo. */
export function getParentZipInternalPath(path: string): string {
  const normalized = normalizeZipInternalPath(path);
  if (!normalized) {
    return '';
  }
  const segments = normalized.split('/').filter(Boolean);
  segments.pop();
  return segments.join('/');
}

/** Nombre del archivo ZIP sin extensión (p. ej. `INGESTA PREPARADA`). */
export function getZipArchiveBaseName(zipPath: string): string {
  const normalized = (zipPath ?? '').trim().replace(/\\/g, '/');
  const fileName = normalized.split('/').filter(Boolean).pop() ?? normalized;
  return fileName.replace(/\.zip$/i, '').trim();
}

/**
 * OAIS DIP: el contenido útil suele estar en `data/<proyecto>` dentro del ZIP,
 * no en la carpeta homónima en la raíz (metadata).
 */
export function resolveOaisZipProjectDataPath(internalPath: string): string {
  const normalized = normalizeZipInternalPath(internalPath);
  if (!normalized) {
    return '';
  }
  if (/^data(\/|$)/i.test(normalized)) {
    return normalized;
  }
  return normalizeZipInternalPath(`data/${normalized}`);
}

/**
 * Al abrir carpeta en raíz del ZIP DIP cuyo nombre coincide con el paquete,
 * saltar a `data/<proyecto>` (equivalente a SIP/.../data/...).
 */
export function resolveOaisZipOpenFolderTarget(
  segment: string,
  currentInternal: string,
  zipArchivePath: string,
): string {
  const target = normalizeZipInternalPath(segment);
  const current = normalizeZipInternalPath(currentInternal);
  if (!target || current || !isUnderDipSegment(zipArchivePath)) {
    return target;
  }
  const firstSegment = target.split('/').filter(Boolean)[0] ?? target;
  const firstLower = firstSegment.toLowerCase();
  if (
    firstLower === 'metadata' ||
    firstLower === 'metada' ||
    firstLower.startsWith('manifiest') ||
    firstLower === 'manifest' ||
    firstLower === 'data' ||
    firstLower === 'nxti' ||
    firstLower === 'nxti_info'
  ) {
    return target;
  }
  const zipBase = getZipArchiveBaseName(zipArchivePath);
  if (zipBase && firstSegment.toUpperCase() === zipBase.toUpperCase()) {
    return resolveOaisZipProjectDataPath(firstSegment);
  }
  return target;
}

/**
 * Si el listado en `internalPath` viene vacío, prueba la ruta espejo bajo `data/`.
 * No aplica a carpetas de empaquetado OAIS (metadata, manifiesto, etc.).
 */
export function resolveOaisZipEmptyFolderFallback(
  internalPath: string,
  zipArchivePath?: string | null,
): string | null {
  const internal = normalizeZipInternalPath(internalPath);
  if (!internal || !zipArchivePath || !isUnderDipSegment(zipArchivePath)) {
    return null;
  }
  if (/^data(\/|$)/i.test(internal)) {
    return null;
  }
  const first = internal.split('/').filter(Boolean)[0]?.toLowerCase() ?? '';
  if (
    first === 'metadata' ||
    first === 'metada' ||
    first.startsWith('manifiest') ||
    first === 'manifest' ||
    first === 'representationsi' ||
    first === 'representationsi_info'
  ) {
    return null;
  }
  const mirror = resolveOaisZipProjectDataPath(internal);
  return mirror && mirror !== internal ? mirror : null;
}

/** Ruta interna al abrir una carpeta dentro del ZIP (acumula niveles). */
export function resolveZipInternalNavigatePath(currentInternal: string, rowSegment: string): string {
  const segment = normalizeZipInternalPath(rowSegment);
  if (!segment) {
    return normalizeZipInternalPath(currentInternal);
  }
  const current = normalizeZipInternalPath(currentInternal);
  if (!current) {
    return segment;
  }
  if (segment === current || segment.startsWith(`${current}/`)) {
    return segment;
  }
  return `${current}/${segment}`.replace(/\/+/g, '/');
}

/** Corrige rutas absolutas que perdieron el `/` inicial (p. ej. `home/egs/...`). */
export function normalizeRepositoryExplorerPath(path: string): string {
  const trimmed = (path ?? '').trim().replace(/\\/g, '/').replace(/\/+/g, '/');
  if (!trimmed || trimmed === REPOSITORY_EXPLORER_ROOT_PATH) {
    return trimmed;
  }
  if (trimmed.startsWith('/')) {
    return trimmed.replace(/\/$/, '') || '/';
  }
  /** Rutas OAIS sin "/" inicial (p. ej. `home` o `home/egs/...`). */
  if (/^home(\/|$)/i.test(trimmed)) {
    return `/${trimmed.replace(/\/$/, '')}`;
  }
  return trimmed.replace(/\/$/, '');
}

/**
 * Ruta efectiva para download/preview/navegación.
 * En listados de subcarpeta el API suele devolver solo el nombre del archivo en `path`.
 */
export function resolveRepositoryItemPath(itemPath: string, listingPath: string): string {
  const raw = (itemPath ?? '').trim().replace(/\\/g, '/');
  if (!raw) {
    return '';
  }
  if (raw === REPOSITORY_EXPLORER_ROOT_PATH) {
    return raw;
  }

  const item = normalizeRepositoryExplorerPath(raw);
  if (!item || item === REPOSITORY_EXPLORER_ROOT_PATH) {
    return item;
  }

  if (/^\/home(\/|$)/i.test(item)) {
    return item;
  }

  const base = normalizeRepositoryExplorerPath(listingPath);
  if (!base || base === REPOSITORY_EXPLORER_ROOT_PATH) {
    return item;
  }

  if (item === base || item.startsWith(`${base}/`)) {
    return item;
  }

  if (!item.includes('/')) {
    return `${base.replace(/\/+$/, '')}/${item}`.replace(/\/+/g, '/');
  }

  return item.startsWith('/') ? item : item;
}

export interface AssignTargetItem {
  path: string;
  name: string;
  isDirectory: boolean;
}

/** Normaliza rutas OAIS para `file_paths` del API de asignación. */
export function normalizeAssignmentPath(path: string): string | null {
  const trimmed = (path ?? '').trim();
  if (!trimmed || trimmed === REPOSITORY_EXPLORER_ROOT_PATH) {
    return null;
  }
  return trimmed.replace(/\/+/g, '/').replace(/\/$/, '') || trimmed;
}

export function isSipFolderPath(path: string): boolean {
  const upper = path.toUpperCase();
  return upper.includes('/SIP/') || upper.endsWith('/SIP');
}

export function isAipFolderPath(path: string): boolean {
  const upper = path.toUpperCase();
  return upper.includes('/AIP/') || upper.endsWith('/AIP');
}

/** Indica si la ruta contiene el segmento OAIS DIP (difusión). */
export function isUnderDipSegment(path: string): boolean {
  const upper = (path ?? '').toUpperCase().replace(/\\/g, '/');
  return upper.includes('/DIP/') || upper.endsWith('/DIP');
}

/**
 * OAIS: solo el contenido bajo DIP (o dentro de un ZIP en DIP) es asignable a consulta.
 * Rechaza rutas con segmentos SIP/AIP a nivel repositorio.
 */
export function isDipAssignablePath(path: string): boolean {
  const virtual = splitZipVirtualAssignmentPath(path);
  if (virtual) {
    return isUnderDipSegment(virtual.zipPath);
  }
  const norm = normalizeAssignmentScopePath(path);
  if (!norm || !isUnderDipSegment(norm)) {
    return false;
  }
  if (isSipFolderPath(norm) || isAipFolderPath(norm)) {
    return false;
  }
  return true;
}

/** Mensaje de bloqueo OAIS para UI; null si la ruta es asignable. */
export function getOaisAssignmentBlockReason(path: string): string | null {
  if (isDipAssignablePath(path)) {
    return null;
  }
  const norm = normalizeAssignmentScopePath(path);
  if (!norm) {
    return 'La ruta seleccionada no es válida para asignar.';
  }
  if (isSipFolderPath(norm)) {
    return 'Según OAIS, el contenido SIP (ingesta) no puede asignarse. Use la carpeta DIP del proyecto.';
  }
  if (isAipFolderPath(norm)) {
    return 'Según OAIS, el contenido AIP (archivo interno) no puede asignarse. Use la carpeta DIP del proyecto.';
  }
  if (!isUnderDipSegment(norm)) {
    return 'Según OAIS, solo el contenido bajo DIP puede asignarse a consulta.';
  }
  return 'La ruta seleccionada no cumple la norma OAIS de asignación (solo DIP).';
}

export function filterOaisAssignableAssignments<T extends { file_path: string }>(
  assignments: T[],
): T[] {
  return assignments.filter((row) => isDipAssignablePath(row.file_path));
}

/** Muestra rutas OAIS acortadas: `/Repositorio/...` sin prefijo del filesystem. */
export function formatRepositoryContextPath(raw: string): string {
  const trimmed = (raw ?? '').trim().replace(/\\/g, '/').replace(/\/+/g, '/');
  if (!trimmed || trimmed === REPOSITORY_EXPLORER_ROOT_PATH) {
    return '(raíz)';
  }
  const lower = trimmed.toLowerCase();
  const marker = '/repositorio/';
  const idx = lower.indexOf(marker);
  if (idx >= 0) {
    return trimmed.slice(idx);
  }
  if (lower.startsWith('repositorio/')) {
    return `/${trimmed}`;
  }
  return trimmed.startsWith('/') ? trimmed : `/${trimmed}`;
}

/** Indica si la ruta apunta a un archivo (no carpeta) por extensión. */
export function isLikelyRepositoryFilePath(path: string): boolean {
  const norm = normalizeRepositoryExplorerPath(path);
  if (!norm || norm.endsWith('/')) {
    return false;
  }
  const last = norm.split('/').pop() ?? '';
  if (!last || last === REPOSITORY_EXPLORER_ROOT_PATH) {
    return false;
  }
  if (last.toLowerCase().endsWith('.zip')) {
    return true;
  }
  return /\.[a-z0-9]{2,8}$/i.test(last);
}

/**
 * Convierte ?target= de Inicio/Bóveda a ruta de listado válida para el API
 * (ruta absoluta OAIS y carpeta contenedora si el target es un archivo).
 */
export function resolveRepositoryDeepLinkListPath(raw: string): string {
  let path = (raw ?? '').trim().replace(/\\/g, '/').replace(/\/+/g, '/');
  if (!path) {
    return '';
  }

  if (/^\/home(\/|$)/i.test(path)) {
    path = normalizeRepositoryExplorerPath(path);
  } else if (/^\/repositorio\//i.test(path)) {
    path = normalizeRepositoryExplorerPath(`/home/egs/datavault${path}`);
  } else if (!path.startsWith('/') && !/^home(\/|$)/i.test(path)) {
    path = normalizeRepositoryExplorerPath(`/home/egs/datavault/Repositorio/${path}`);
  } else {
    path = normalizeRepositoryExplorerPath(path);
  }

  if (isLikelyRepositoryFilePath(path)) {
    return getParentRepositoryExplorerPath(path);
  }
  if (path.toLowerCase().endsWith('.zip')) {
    return getParentRepositoryExplorerPath(path);
  }
  return path;
}

/** Paridad legacy: solo carpetas → true; solo archivos o mezcla → false. */
export function resolveAssignIsDirectoryFlag(items: AssignTargetItem[]): boolean {
  const hasDir = items.some((i) => i.isDirectory);
  const hasFile = items.some((i) => !i.isDirectory);
  if (hasDir && !hasFile) {
    return true;
  }
  return false;
}

export function buildAssignTargetItems(
  items: AssignTargetItem[],
): { paths: string[]; items: AssignTargetItem[] } {
  const normalized: AssignTargetItem[] = [];
  const seen = new Set<string>();
  for (const item of items) {
    const path = normalizeAssignmentPath(item.path);
    if (!path || seen.has(path)) {
      continue;
    }
    seen.add(path);
    normalized.push({ ...item, path });
  }
  return { paths: normalized.map((i) => i.path), items: normalized };
}

/** Normaliza ruta de asignación del API a OAIS absoluta (sin mover archivos a la carpeta padre). */
export function normalizeAssignmentRecordPath(path: string | null | undefined): string {
  const trimmed = (path ?? '').trim().replace(/\\/g, '/').replace(/\/+/g, '/').replace(/\/$/, '');
  if (!trimmed || trimmed === REPOSITORY_EXPLORER_ROOT_PATH) {
    return '';
  }
  const lower = trimmed.toLowerCase();
  if (lower.startsWith('/repositorio/')) {
    return normalizeRepositoryExplorerPath(`/home/egs/datavault${trimmed}`);
  }
  if (lower.startsWith('repositorio/')) {
    return normalizeRepositoryExplorerPath(`/home/egs/datavault/${trimmed}`);
  }
  if (!trimmed.startsWith('/') && !/^home(\/|$)/i.test(trimmed)) {
    return normalizeRepositoryExplorerPath(`/home/egs/datavault/Repositorio/${trimmed}`);
  }
  return normalizeRepositoryExplorerPath(trimmed);
}

/** Ruta OAIS normalizada para comparar asignaciones y navegación acotada. */
export function normalizeAssignmentScopePath(path: string | null | undefined): string {
  return normalizeAssignmentRecordPath(path);
}

/** Separa `archivo.zip/carpeta` o `archivo.zip::carpeta` (API search) en ZIP + ruta interna. */
export function splitZipVirtualAssignmentPath(path: string): {
  zipPath: string;
  internalPath: string;
} | null {
  const norm = normalizeAssignmentScopePath(path);
  if (!norm) {
    return null;
  }
  const lower = norm.toLowerCase();

  const colonMarker = '.zip::';
  const colonIdx = lower.indexOf(colonMarker);
  if (colonIdx >= 0) {
    const zipPath = norm.slice(0, colonIdx + 4);
    const internalPath = normalizeZipInternalPath(norm.slice(colonIdx + colonMarker.length));
    if (!zipPath) {
      return null;
    }
    return { zipPath, internalPath };
  }

  const marker = '.zip/';
  const idx = lower.indexOf(marker);
  if (idx < 0) {
    return null;
  }
  const zipPath = norm.slice(0, idx + 4);
  const internalPath = normalizeZipInternalPath(norm.slice(idx + 5));
  if (!zipPath || !internalPath) {
    return null;
  }
  return { zipPath, internalPath };
}

/**
 * Ruta canónica de un hit de `GET /repository/search` (incl. contenido en ZIP).
 * El API suele devolver `zip_path` + `zip_internal_path` en lugar de `path` completo.
 */
export function resolveRepositorySearchHitPath(raw: {
  path?: string | null;
  name?: string | null;
  zip_path?: string | null;
  zip_internal_path?: string | null;
  internal_path?: string | null;
}): string {
  const zipPathRaw = String(raw.zip_path ?? '').trim();
  if (zipPathRaw) {
    const inner = normalizeZipInternalPath(
      extractZipInternalRelativePath(raw.zip_internal_path) ||
        extractZipInternalRelativePath(raw.internal_path) ||
        String(raw.zip_internal_path ?? raw.internal_path ?? '').trim(),
    );
    const zipNorm = normalizeRepositoryExplorerPath(zipPathRaw.replace(/\\/g, '/'));
    return inner ? `${zipNorm}::${inner}` : zipNorm;
  }

  const pathRaw = String(raw.path ?? '').trim();
  if (pathRaw) {
    const fromEmbed = extractZipInternalRelativePath(pathRaw);
    if (fromEmbed && /\.zip(::|\/)/i.test(pathRaw)) {
      return normalizeRepositoryExplorerPath(pathRaw.replace(/\\/g, '/'));
    }
    return pathRaw;
  }

  return String(raw.name ?? '').trim();
}

/** Ruta de asignación para un elemento dentro de un ZIP (`archivo.zip/ruta/interna`). */
export function buildZipVirtualAssignmentPath(
  zipArchivePath: string,
  internalPath?: string,
): string {
  const zip = normalizeAssignmentScopePath(zipArchivePath);
  const inner = normalizeZipInternalPath(internalPath ?? '');
  if (!zip) {
    return inner;
  }
  if (!inner) {
    return zip;
  }
  return `${zip}/${inner}`;
}

export function isZipArchiveAssignmentPath(path: string): boolean {
  const norm = normalizeAssignmentScopePath(path).toLowerCase();
  return norm.endsWith('.zip');
}
